import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink, Link2, Unlink } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import type {
  LinkedChannels,
  WhatsAppLinkOffer,
} from '@/Domain/api/ChannelIdentity/ChannelIdentity.api';
import { ReadDocumentationButton } from '@/Domain/components/Integration/components/ReadDocumentationButton';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Skeleton } from '@/Domain/components/Shared/Skeleton/Skeleton';

// The only panel here whose documentation is a different page from the sheet footer's.
const DOCS_HREF = 'https://docs.quickchat.ai/support';
// The link is completed on the user's phone, so the desktop only learns it happened by asking.
const LINK_POLL_INTERVAL_MS = 5000;

/**
 * Linking your own WhatsApp number to Quickchat's Support AI.
 *
 * A client tab in the account-MCP sheet, beside Claude and ChatGPT: an account-level
 * way to reach your Quickchat account from outside the dashboard, not a channel you
 * deploy your own AI Agent to. The tab label is the heading, so this renders none.
 *
 * `enabled` gates the query so a closed host sheet does not poll.
 */
export const SupportWhatsAppConnect = ({ enabled }: { enabled: boolean }) => {
  const { t } = useTranslation(['support_whatsapp', 'integrations']);

  // Held locally rather than read off the mutation: an offer is single-use and
  // short-lived, so it has to be discardable the moment it stops being usable,
  // and a mounted mutation would keep handing back the dead one.
  const [offer, setOffer] = useState<WhatsAppLinkOffer | null>(null);
  const linkedStateWhenOffered = useRef<string | null>(null);
  const autoStarted = useRef(false);
  const openedAt = useRef(0);

  const { data, isLoading, isError, refetch, isFetching, dataUpdatedAt } = useQuery<LinkedChannels>(
    {
      queryKey: [QueryKey.channelIdentity.list],
      enabled,
      // staleTime is 0 in the query defaults, so mount and window focus already
      // reconcile. Polling covers the case they miss: the sheet sitting open on a
      // focused tab while the user completes the link on their phone.
      refetchInterval: offer ? LINK_POLL_INTERVAL_MS : false,
    },
  );
  // Not a count: completing a link can update an identity in place rather than add
  // one, so the list can change without growing. The timestamps move when it does.
  const linkedState = (data?.linked ?? [])
    .map((linked) => `${linked.id}:${linked.linked_at}`)
    .sort()
    .join('|');

  const startLink = useMutation<WhatsAppLinkOffer, unknown, void>({
    mutationKey: [QueryKey.channelIdentity.whatsappLink],
    onSuccess: (created) => {
      linkedStateWhenOffered.current = linkedState;
      setOffer(created);
    },
  });
  const revoke = useMutation<unknown, unknown, number>({
    mutationKey: [QueryKey.channelIdentity.revoke],
  });

  // An offer dies three ways, and every one of them has to take the QR with it.
  // The timer is the tidy-up; the render below decides, because a suspended tab
  // fires its timers late and must not show a dead code even for one frame.
  useEffect(() => {
    if (!offer) return;
    const remainingMs = new Date(offer.expires_at).getTime() - Date.now();
    if (Number.isNaN(remainingMs) || remainingMs <= 0) {
      setOffer(null);
      return;
    }
    const timer = setTimeout(() => setOffer(null), remainingMs);
    return () => clearTimeout(timer);
  }, [offer]);

  // Any change retires the offer, not only an addition. A disconnect made in this
  // same sheet also counts: the list moved under the code, so showing it on is a
  // claim we can no longer make.
  useEffect(() => {
    if (!offer || linkedStateWhenOffered.current === null) return;
    if (linkedState !== linkedStateWhenOffered.current) setOffer(null);
  }, [offer, linkedState]);

  // The mutation result is never read, but leaving a spent offer sitting in it
  // is one more copy of a live credential than this component needs.
  useEffect(() => {
    if (!offer && startLink.data) startLink.reset();
  }, [offer, startLink]);

  // The host sheet closing has to retire the offer for the same reason closing
  // the old standalone sheet did: the code outlives the UI otherwise.
  useEffect(() => {
    if (enabled) {
      // Stamped on open so the auto-start below can tell this opening's list
      // from the one cached by the previous one.
      openedAt.current = Date.now();
      return;
    }
    setOffer(null);
    startLink.reset();
    autoStarted.current = false;
    // startLink.reset is stable; depending on the mutation object would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const canStartLink = Boolean(data?.whatsapp_available);
  const unavailable = data !== undefined && !data.whatsapp_available;
  const liveOffer = offer && new Date(offer.expires_at).getTime() > Date.now() ? offer : null;
  const hasLinked = Boolean(data?.linked.length);

  // The QR is the point of this section, so it is on screen as soon as the sheet
  // is, with no click to get there. Safe to mint on open because the server
  // reuses an open ceremony rather than issuing a code per request, so repeated
  // opens converge on one row and one code.
  //
  // Not when something is already linked: that visit is about managing what is
  // there, and a code nobody asked for is noise. Once per open, so an expired or
  // failed offer falls back to the explicit button instead of minting forever.
  //
  // Waits for a list fetched for THIS opening. staleTime 0 means the query still
  // serves the previous opening's cached array while it refetches, so a sender
  // who closed the sheet and finished linking on their phone would otherwise
  // reopen to `linked: []`, and be minted a code they never asked for before the
  // fresh response arrived. The explicit button stays independent of this.
  const listIsForThisOpening = !isFetching && dataUpdatedAt >= openedAt.current;
  useEffect(() => {
    if (!enabled || autoStarted.current || !listIsForThisOpening) return;
    if (!canStartLink || hasLinked || liveOffer || startLink.isPending) return;
    autoStarted.current = true;
    startLink.mutate();
  }, [enabled, canStartLink, hasLinked, liveOffer, listIsForThisOpening, startLink]);

  return (
    <div className="space-y-3" data-testid="support-whatsapp-connect">
      <p className="text-sm text-muted-foreground">{t('support_whatsapp:overview')}</p>

      {unavailable ? (
        <p className="text-sm text-muted-foreground" data-testid="support-whatsapp-unavailable">
          {t('support_whatsapp:unavailable')}
        </p>
      ) : liveOffer ? (
        // ph-no-capture covers the whole block, not just the anchor: autocapture,
        // heatmaps and dead-click capture are all enabled, and between them they
        // emit the href, the element text and the external-click URL. Every one of
        // those carries a live linking code that would let a reader of analytics
        // bind their own WhatsApp number to this account.
        // data-dd-privacy="hidden" excludes the subtree from Datadog session
        // replay entirely: the QR encodes the code-bearing URL into SVG path
        // data, which the global 'mask' level does not redact, and 10% of
        // sessions are recorded. ph-no-capture only opts out of PostHog.
        <div
          className="ph-no-capture flex flex-col items-center gap-4 rounded-lg border border-border bg-card p-6"
          data-dd-privacy="hidden"
          data-testid="support-whatsapp-offer"
        >
          {/* Self-contained on purpose. marginSize=4 is the QR spec's quiet zone,
              rendered inside the SVG with an explicit white bgColor, so
              scannability never depends on the surrounding surface. It used to:
              the wrapper's `bg-white` supplied the only quiet zone, and
              `.dark .bg-white` in app.css force-overrides that to `var(--card)`,
              so in dark mode the black modules met a dark background with no
              margin at all and no scanner could find the finder patterns. The
              wrapper keeps a literal white that the dark rule does not target. */}
          <div className="rounded-lg bg-[#ffffff] p-3">
            <QRCodeSVG
              value={liveOffer.deep_link_url}
              size={168}
              marginSize={4}
              bgColor="#ffffff"
              fgColor="#000000"
            />
          </div>
          <p className="text-center text-sm text-muted-foreground text-pretty">
            {t('support_whatsapp:scan_instruction')}
          </p>
          <p className="text-center text-sm text-muted-foreground">
            {t('support_whatsapp:code_label')}{' '}
            <strong
              className="font-mono tracking-widest text-foreground"
              data-testid="support-whatsapp-user-code"
            >
              {liveOffer.user_code}
            </strong>
          </p>
          <Button
            asChild
            variant="secondary"
            size="sm"
            className="w-full"
            data-testid="support-whatsapp-open-link"
          >
            <a href={liveOffer.deep_link_url} target="_blank" rel="noreferrer">
              <ExternalLink className="size-4" />
              {t('support_whatsapp:open_whatsapp')}
            </a>
          </Button>
        </div>
      ) : (
        <Button
          type="button"
          variant="dark"
          size="sm"
          className="w-full"
          // Held until availability is known: an account that cannot link
          // would otherwise send a POST that is guaranteed to fail.
          disabled={startLink.isPending || !canStartLink}
          onClick={() => startLink.mutate()}
          data-testid="support-whatsapp-start-link"
        >
          <Link2 className="size-4" />
          {hasLinked ? t('support_whatsapp:link_another') : t('support_whatsapp:start_link')}
        </Button>
      )}

      <div className="space-y-2" data-testid="support-whatsapp-linked-section">
        <p className="text-sm font-medium text-foreground">
          {t('support_whatsapp:linked_channels')}
        </p>
        {isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : isError ? (
          // A failed load must not impersonate an empty account: without this
          // branch the section showed "no linked channels" with the start
          // button dead until some background refetch happened.
          <div
            className="flex flex-col items-start gap-2"
            data-testid="support-whatsapp-load-error"
          >
            <p className="text-sm text-muted-foreground">{t('support_whatsapp:load_error')}</p>
            <Button type="button" variant="secondary" size="sm" onClick={() => refetch()}>
              {t('support_whatsapp:retry')}
            </Button>
          </div>
        ) : data?.linked.length ? (
          <ul className="space-y-2">
            {data.linked.map((linked) => (
              <li
                key={linked.id}
                className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3"
                data-testid={`support-whatsapp-linked-${linked.id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">{linked.masked_sender}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('support_whatsapp:linked_on', {
                      date: new Date(linked.linked_at).toLocaleDateString(),
                    })}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(linked.id)}
                  data-testid={`support-whatsapp-disconnect-${linked.id}`}
                >
                  <Unlink className="size-4" />
                  {t('integrations:disconnect')}
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="support-whatsapp-empty">
            {t('support_whatsapp:no_linked_channels')}
          </p>
        )}
      </div>

      <ReadDocumentationButton href={DOCS_HREF} />
    </div>
  );
};
